/**
 * Proposals inbox (`/proposals`) — docs/08-frontend-and-ux.md §3.9.
 *
 * The tab set is role-aware: "Awaiting my vote" exists only for seat holders and
 * is their default; everyone else lands on "Open". The tab lives in the URL, so
 * a link to the closed tab is a link someone can send.
 *
 * Sort is closest-deadline-first within each tab (§3.9), which is `closes_at`
 * ascending — the api's non-default sort, asked for explicitly.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Proposal } from '@famtree/shared';
import { proposalsApi, type ProposalListParams } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { SeatStrip, countdown, decisiveFact, statusClass, tallySentence } from './tally.js';
import ui from '../../ui/components.module.css';
import styles from './proposals.module.css';

type Tab = 'needs_my_vote' | 'open' | 'mine' | 'closed';

const CLOSED_STATUSES = ['approved', 'rejected', 'expired', 'withdrawn'];

/** The list query each tab stands for (§3.9). */
function paramsForTab(tab: Tab): ProposalListParams {
  if (tab === 'closed') {
    return { filter: 'all', status: CLOSED_STATUSES, sort: '-opened_at', limit: 50 };
  }
  return { filter: tab, sort: 'closes_at', limit: 50 };
}

export function ProposalsInboxPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();

  const hasSeat = Boolean(user?.seat);
  const tabs: Tab[] = hasSeat
    ? ['needs_my_vote', 'open', 'mine', 'closed']
    : ['open', 'mine', 'closed'];
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && tabs.includes(requested) ? requested : tabs[0]!;

  const proposals = useQuery({
    queryKey: ['proposals', tab],
    queryFn: () => proposalsApi.list(paramsForTab(tab)),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className={styles.header}>
        <h1>{t('proposals.title')}</h1>
        <span className={styles.spacer} />
        {user?.can_propose && (
          <Link className={ui.button} to="/people/new">
            {t('people.addPerson')}
          </Link>
        )}
      </div>

      <div className={styles.tabs} role="tablist">
        {tabs.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={entry === tab}
            className={`${styles.tab} ${entry === tab ? styles.tabActive : ''}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('tab', entry);
              setParams(next, { replace: true });
            }}
          >
            {t(`proposals.tabs.${entry}`)}
          </button>
        ))}
      </div>

      {proposals.isPending && <p className={ui.muted}>{t('app.loading')}</p>}
      {proposals.isError && (
        <div className={ui.problem}>
          <p>{problemMessage(proposals.error, t)}</p>
          <button
            className={ui.buttonSecondary}
            type="button"
            onClick={() => void proposals.refetch()}
          >
            {t('app.retry')}
          </button>
        </div>
      )}

      {proposals.data?.data.length === 0 && (
        <p className={ui.muted}>{t(`proposals.empty.${tab}`)}</p>
      )}

      {proposals.data && proposals.data.data.length > 0 && (
        <ul className={styles.list}>
          {proposals.data.data.map((proposal) => (
            <li key={proposal.id}>
              <ProposalCard proposal={proposal} />
            </li>
          ))}
        </ul>
      )}

      {proposals.data?.page.has_more && <p className={ui.muted}>{t('people.moreAvailable')}</p>}
    </div>
  );
}

function ProposalCard({ proposal }: { proposal: Proposal }): JSX.Element {
  const { t } = useTranslation();
  const deadline = countdown(proposal.closes_at, t);
  const fact = decisiveFact(proposal.tally, proposal.status, t);

  return (
    <Link className={styles.card} to={`/proposals/${proposal.id}`}>
      <span className={styles.cardTitle}>
        <span className={styles.kind}>{t(`enums.proposal_kind.${proposal.kind}`)}</span>
        <span>{proposal.summary}</span>
        <span className={styles.spacer} />
        <span className={`${styles.status} ${statusClass(proposal.status)}`}>
          {t(`enums.proposal_status.${proposal.status}`)}
        </span>
      </span>

      <p className={styles.cardMeta}>
        {proposal.status === 'open' ? (
          <span className={`${styles.deadline} ${deadline.urgent ? styles.deadlineUrgent : ''}`}>
            {deadline.label}
          </span>
        ) : (
          <span>{t('proposals.decidedOn', { date: formatDate(proposal.decided_at) })}</span>
        )}
      </p>

      <SeatStrip tally={proposal.tally} />
      <p className={styles.tallyLine}>
        {tallySentence(proposal.tally, t)}
        {' · '}
        {proposal.my_vote
          ? t('proposals.youVoted', { choice: t(`enums.vote_choice.${proposal.my_vote.choice}`) })
          : t('proposals.youHaveNotVoted')}
      </p>
      {fact && <p className={styles.tallyLine}>{fact}</p>}
    </Link>
  );
}

/** Dates are rendered in the viewer's locale; only the *value* comes from the api. */
function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
